// ConfirmDialog — destructive-action confirmation per spec §18.5.
//
// Differs from the regular Modal in three ways:
//   1. Cannot be dismissed by clicking the backdrop (the user must explicitly
//      cancel or confirm — protects against accidental confirmations).
//   2. Confirm button uses --clay (or --ink for non-destructive) per spec.
//   3. Auto-focuses the cancel button so Enter does NOT confirm by default.

import { useEffect, useRef, type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps) {
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    // Auto-focus the cancel button so Enter doesn't accidentally confirm.
    cancelBtnRef.current?.focus();
    // Esc cancels.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="modal-backdrop confirm-backdrop" role="presentation">
      <div
        className="modal modal--sm confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-body"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h3 id="confirm-title">{title}</h3>
        </header>
        <div className="modal__body" id="confirm-body">{body}</div>
        <footer className="modal__footer">
          <button
            ref={cancelBtnRef}
            type="button"
            className="btn btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'btn btn--warm' : 'btn btn--primary'}
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
